import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PinVerificationModal from "../components/PinVerificationModal";

export default function Transfer() {
  const [searchType, setSearchType] = useState("email");
  const [searchValue, setSearchValue] = useState("");
  const [recipient, setRecipient] = useState(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  // PIN Verification
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // User balance
  const [balance, setBalance] = useState(0);

  const navigate = useNavigate();
  
  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    checkTwoFactorStatus();
    loadBalance();
  }, []);

  const checkTwoFactorStatus = async () => {
    try {
      const res = await api.get("/TwoFactor/status");
      setTwoFactorEnabled(res.data.isEnabled);
    } catch (err) {
      setTwoFactorEnabled(false);
    }
  };

  const loadBalance = async () => {
    try {
      const res = await api.get("/Wallet/balance");
      setBalance(res.data.balance);
    } catch (err) {
      console.error("Failed to load balance");
    }
  };

  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Search for recipient (called automatically)
  const searchRecipient = async (value, type) => {
    if (!value) {
      setRecipient(null);
      setError("");
      return;
    }

    // Validate input before searching
    if (type === "email") {
      // Basic email validation - must have @ and at least one character before and after
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setRecipient(null);
        return; // Don't show error while typing, just don't search
      }
    } else {
      // Account number should be at least 10 digits
      if (value.length < 10) {
        setRecipient(null);
        return;
      }
    }

    setSearching(true);
    setError("");

    try {
      const queryParam = type === "account" 
        ? `accountNumber=${value}`
        : `email=${encodeURIComponent(value)}`;
      
      const res = await api.get(`/Wallet/lookup?${queryParam}`);
      setRecipient(res.data);
      setError("");
    } catch (err) {
      setRecipient(null);
      // Only show error if input looks complete
      if ((type === "email" && value.includes("@") && value.includes(".")) || 
          (type === "account" && value.length >= 10)) {
        setError(err.response?.data?.message || "Recipient not found");
      }
    } finally {
      setSearching(false);
    }
  };

  // Debounced search - waits 500ms after user stops typing
  const debouncedSearch = useCallback(
    debounce((value, type) => {
      searchRecipient(value, type);
    }, 500),
    []
  );

  // Handle input change - triggers auto-search
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    setRecipient(null); // Clear previous recipient while typing
    setError(""); // Clear error while typing
    
    // Trigger debounced search
    debouncedSearch(value, searchType);
  };

  // Handle search type change
  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    setRecipient(null);
    setSearchValue("");
    setError("");
  };

  // Step 1: User clicks "Transfer" button
  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!recipient) {
      setError("Please find a recipient first");
      return;
    }

    if (!amount || Number(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    // Store pending transfer details - send both email and account number
    const transferData = {
      recipientEmail: searchType === "email" ? searchValue : null,
      toAccountNumber: recipient.accountNumber,
      amount: Number(amount),
      description: description || "Fund transfer"
    };

    setPendingTransfer(transferData);

    // Always show PIN modal for security
    console.log("🔐 Opening PIN modal for transfer:", transferData);
    setShowPinModal(true);
  };

  // Step 2: After PIN verified
  const handlePinVerified = () => {
    if (pendingTransfer) {
      executeTransfer(pendingTransfer);
    }
  };

  // Step 3: Execute the transfer
  const executeTransfer = async (transferData) => {
    setLoading(true);
    setError("");

    try {
      const res = await api.post("/Wallet/transfer", transferData);
      
      setMessage(`✅ Transfer successful! Sent ₦${res.data.transferAmount?.toLocaleString()} to ${res.data.recipientName || recipient?.fullName}`);
      setRecipient(null);
      setSearchValue("");
      setAmount("");
      setDescription("");
      setPendingTransfer(null);
      setBalance(res.data.newBalance);
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => navigate("/dashboard"), 3000);
    } catch (err) {
      console.error("Transfer error:", err);
      setError(err.response?.data?.message || "Transfer failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amt) => `₦${Number(amt || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>📤 Transfer Funds</h1>
        <p style={styles.subtitle}>Send money to another user</p>

        {/* Balance Display */}
        <div style={styles.balanceCard}>
          <span style={styles.balanceLabel}>Your Balance:</span>
          <span style={styles.balanceValue}>{formatCurrency(balance)}</span>
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {message && <div style={styles.success}>{message}</div>}

        {!message && (
          <>
            {/* Step 1: Find Recipient */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>1. Find Recipient</h3>
              
              <div style={styles.searchTypeButtons}>
                <button
                  type="button"
                  onClick={() => handleSearchTypeChange("email")}
                  style={{
                    ...styles.searchTypeBtn,
                    ...(searchType === "email" ? styles.searchTypeBtnActive : {})
                  }}
                >
                  By Email
                </button>
                <button
                  type="button"
                  onClick={() => handleSearchTypeChange("account")}
                  style={{
                    ...styles.searchTypeBtn,
                    ...(searchType === "account" ? styles.searchTypeBtnActive : {})
                  }}
                >
                  By Account Number
                </button>
              </div>

              <div style={styles.inputWrapper}>
                <input
                  type={searchType === "email" ? "email" : "text"}
                  placeholder={searchType === "email" ? "Enter recipient's email" : "Enter account number"}
                  value={searchValue}
                  onChange={handleSearchInputChange}
                  style={styles.input}
                  disabled={loading}
                />
                {searching && (
                  <div style={styles.searchingIndicator}>
                    <span style={styles.spinner}></span>
                  </div>
                )}
              </div>
              
              {/* Helper text */}
              <p style={styles.helperText}>
                {searchType === "email" 
                  ? "Enter a valid email address to find recipient" 
                  : "Enter 10-digit account number to find recipient"}
              </p>

              {recipient && (
                <div style={styles.recipientCard}>
                  <div style={styles.recipientAvatar}>
                    {recipient.fullName?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div style={styles.recipientInfo}>
                    <div style={styles.recipientName}>{recipient.fullName}</div>
                    <div style={styles.recipientAccount}>{recipient.accountNumber}</div>
                  </div>
                  <div style={styles.recipientCheck}>✓</div>
                </div>
              )}
            </div>

            {/* Step 2: Enter Amount */}
            {recipient && (
              <form onSubmit={handleSubmit} style={styles.section}>
                <h3 style={styles.sectionTitle}>2. Enter Amount</h3>
                
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Amount (₦)</label>
                  <input
                    type="number"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                    min="1"
                    style={styles.amountInput}
                    disabled={loading}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Description (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., Payment, Gift, Loan, etc."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    style={styles.input}
                    disabled={loading}
                  />
                </div>

                {/* PIN Notice */}
                <div style={styles.pinNotice}>
                  🔐 You will be asked to enter your transaction PIN to authorize this transfer
                </div>

                <button 
                  type="submit" 
                  style={{
                    ...styles.submitButton,
                    opacity: loading || !amount ? 0.6 : 1
                  }}
                  disabled={loading || !amount}
                >
                  {loading ? "Processing..." : `Transfer ${amount ? formatCurrency(amount) : ""}`}
                </button>
              </form>
            )}
          </>
        )}

        <button
          onClick={() => navigate("/dashboard")}
          style={styles.backButton}
          disabled={loading}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* PIN Verification Modal */}
      <PinVerificationModal
        isOpen={showPinModal}
        onClose={() => {
          setShowPinModal(false);
          setPendingTransfer(null);
        }}
        onVerified={handlePinVerified}
        transactionType="Transfer"
        amount={pendingTransfer?.amount}
        title="Authorize Transfer"
      />

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    padding: "20px",
  },
  card: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    width: "100%",
    maxWidth: "500px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "5px",
    color: "#333",
  },
  subtitle: {
    color: "#666",
    marginBottom: "20px",
  },
  balanceCard: {
    backgroundColor: "#e3f2fd",
    padding: "12px 15px",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px"
  },
  balanceLabel: {
    fontSize: "14px",
    color: "#1565c0"
  },
  balanceValue: {
    fontWeight: "bold",
    fontSize: "18px",
    color: "#1565c0"
  },
  section: {
    marginBottom: "20px"
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "12px"
  },
  searchTypeButtons: {
    display: "flex",
    gap: "10px",
    marginBottom: "12px"
  },
  searchTypeBtn: {
    flex: 1,
    padding: "10px",
    border: "1px solid #ddd",
    backgroundColor: "white",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    transition: "all 0.2s"
  },
  searchTypeBtnActive: {
    backgroundColor: "#007bff",
    color: "white",
    borderColor: "#007bff"
  },
  inputWrapper: {
    position: "relative",
    width: "100%"
  },
  searchingIndicator: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)"
  },
  spinner: {
    display: "inline-block",
    width: "20px",
    height: "20px",
    border: "2px solid #ddd",
    borderTopColor: "#007bff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },
  helperText: {
    fontSize: "12px",
    color: "#888",
    marginTop: "6px",
    marginBottom: "0"
  },
  recipientCard: {
    display: "flex",
    alignItems: "center",
    padding: "12px",
    backgroundColor: "#d4edda",
    borderRadius: "8px",
    marginTop: "12px",
    border: "1px solid #c3e6cb"
  },
  recipientAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#28a745",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: "bold",
    marginRight: "12px"
  },
  recipientInfo: {
    flex: 1
  },
  recipientName: {
    fontWeight: "bold",
    fontSize: "14px",
    color: "#155724"
  },
  recipientAccount: {
    color: "#155724",
    fontSize: "12px",
    fontFamily: "monospace"
  },
  recipientCheck: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "#28a745",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px"
  },
  error: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "12px",
    borderRadius: "8px",
    marginBottom: "15px",
  },
  success: {
    backgroundColor: "#d4edda",
    color: "#155724",
    padding: "12px",
    borderRadius: "8px",
    marginBottom: "15px",
  },
  inputGroup: {
    marginBottom: "15px",
  },
  label: {
    display: "block",
    marginBottom: "6px",
    fontWeight: "500",
    color: "#333",
    fontSize: "14px"
  },
  input: {
    width: "100%",
    padding: "12px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    boxSizing: "border-box",
  },
  amountInput: {
    width: "100%",
    padding: "15px",
    fontSize: "20px",
    fontWeight: "bold",
    textAlign: "center",
    border: "1px solid #ddd",
    borderRadius: "8px",
    boxSizing: "border-box",
  },
  pinNotice: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
    padding: "12px 15px",
    borderRadius: "8px",
    marginBottom: "15px",
    fontSize: "13px"
  },
  pinWarning: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "12px 15px",
    borderRadius: "8px",
    marginBottom: "15px",
    fontSize: "13px"
  },
  link: {
    color: "#856404",
    fontWeight: "500"
  },
  submitButton: {
    width: "100%",
    padding: "14px",
    fontSize: "16px",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#007bff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  backButton: {
    width: "100%",
    padding: "12px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#666",
    backgroundColor: "transparent",
    border: "1px solid #ddd",
    borderRadius: "8px",
    cursor: "pointer",
    marginTop: "15px",
  },
};

